import { useCallback, useEffect, useRef } from "react";

/**
 * Opens a context menu from a right-click or a touch long-press.
 *
 * `contextmenu` alone is not enough: desktop browsers and Android fire it, but
 * iOS Safari does not raise it for ordinary elements, so touch is tracked
 * explicitly. Pointer events cover mouse, touch and pen through one path, and
 * the pointer type decides which gesture applies.
 */
const LONG_PRESS_MS = 450;
/** Movement past this is a scroll, not a press. */
const MOVE_TOLERANCE_PX = 10;

export type ContextMenuHandlers = {
  onContextMenu: (event: React.MouseEvent) => void;
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onClick: (event: React.MouseEvent) => boolean;
};

export function useContextMenu(open: () => void): ContextMenuHandlers {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  // A fired long-press must not also count as a tap on the row underneath.
  const suppressClick = useRef(false);

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    origin.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  return {
    onContextMenu: (event) => {
      event.preventDefault();
      cancel();
      open();
    },
    onPointerDown: (event) => {
      if (event.pointerType === "mouse") {
        return;
      }
      origin.current = { x: event.clientX, y: event.clientY };
      timer.current = setTimeout(() => {
        timer.current = null;
        suppressClick.current = true;
        // A short tick makes the press feel like it registered, where supported.
        navigator.vibrate?.(12);
        open();
      }, LONG_PRESS_MS);
    },
    onPointerMove: (event) => {
      const start = origin.current;
      if (!start) {
        return;
      }
      if (
        Math.abs(event.clientX - start.x) > MOVE_TOLERANCE_PX ||
        Math.abs(event.clientY - start.y) > MOVE_TOLERANCE_PX
      ) {
        cancel();
      }
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    /** Returns false when the click was the tail of a long-press. */
    onClick: () => {
      if (suppressClick.current) {
        suppressClick.current = false;
        return false;
      }
      return true;
    },
  };
}
